import { Color, Spacing } from '@persona-id/design-tokens';
import { EditIcon } from '@persona-id/design-tokens/icons';
import { kebabCase } from 'lodash';
import { ChangeEvent, Fragment, useEffect, useMemo, VFC } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import OptionsFieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/OptionsFieldMappingsPanel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { ExternalIntegrationProductivityHubspotOAuth } from 'dashboard/models/external-integration';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionHubspotOAuthUpdateObjectV1 } from 'dashboard/models/workflow-version';
import { loadExternalIntegration } from 'dashboard/redux/thunk-actions/external-integrations';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { FontWeight } from 'design-system/variables/text';
import useApiCallback from 'lib/hooks/useApiCallback';
import useExternalIntegration from 'lib/hooks/useExternalIntegration';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { JsonSchemaTypes } from 'lib/json-schema';

const InputFieldsHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const HorizontalScrollableBox = styled.div`
  font-family: ${({ theme }) => theme.text.fontFamily.mono};
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  font-weight: ${FontWeight.Medium};
  padding: ${Spacing[4]};
  background-color: ${Color.CoreSlate50};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  overflow-x: scroll;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const Key = styled.div`
  font-weight: ${FontWeight.Bold};
  white-space: nowrap;
`;

const Value = styled.div`
  white-space: nowrap;
  margin-left: ${Spacing[2]};
  padding-right: ${Spacing[4]}; // Extra space when scrolling to right edge
`;

const NEW_HUBSPOT_INTEGRATION = 'marketplace-management-rediret';

interface ParamsHubspotOAuthUpdateObjectV1Props extends ParamsBaseProps, PropsFromRedux {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionHubspotOAuthUpdateObjectV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionHubspotOAuthUpdateObjectV1['params']) => void;
}

const HubspotObjectTypes: Record<
  WorkflowActionHubspotOAuthUpdateObjectV1['params']['object-type'],
  string
> = {
  companies: 'Company',
  contacts: 'Contact',
  deals: 'Deal',
  line_items: 'Line Item',
  quotes: 'Quote',
  tickets: 'Ticket',
};

const ParamsHubspotOAuthUpdateObjectV1: VFC<ParamsHubspotOAuthUpdateObjectV1Props> = ({
  step,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  loadExternalIntegration,
  updateActionParams,
  updateStep,
}) => {
  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const [hubspotExternalIntegrations, { isLoading: isExtIntLoading }] = useExternalIntegration([
    ApiModel.ExternalIntegrationProductivityHubspotOAuth,
  ]);

  const recordObjectOptions = useMemo(
    () =>
      Object.entries(HubspotObjectTypes).map(([value, label]) => ({
        value,
        label,
      })),
    [HubspotObjectTypes],
  );

  const { callback: loadExternalIntegrationDetails, loading: isDetailsLoading } = useApiCallback(
    async (integrationId: string, requestedDetail: string) => {
      await loadExternalIntegration(integrationId, requestedDetail);
    },
    [],
  );

  const isLoading = isExtIntLoading || isDetailsLoading;

  const getRequestedAttributes = () =>
    hubspotExternalIntegrations.find(
      (i): i is ExternalIntegrationProductivityHubspotOAuth =>
        i.id === params['external-integration-id'],
    )?.attributes?.['object-type-properties']?.[kebabCase(params['object-type'])];

  useEffect(() => {
    if (params['external-integration-id'] && params['object-type']) {
      getRequestedAttributes()
        ? null
        : loadExternalIntegrationDetails(params['external-integration-id'], params['object-type']);
    }
  }, [params['external-integration-id'], params['object-type']]);

  const propertiesOptions = useMemo(
    () => getRequestedAttributes(),
    [hubspotExternalIntegrations, params['object-type']],
  );

  const updateHubspotObjectParams = <
    Key extends keyof WorkflowActionHubspotOAuthUpdateObjectV1['params'],
  >(
    key: Key,
    value: WorkflowActionHubspotOAuthUpdateObjectV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  const [showEditPanel, { disable: closeEditPanel, toggle: toggleEditPanel }] = useToggle(false);

  const hubspotInstanceOptions = [
    ...hubspotExternalIntegrations.map((integration) => ({
      label: integration.attributes.name,
      value: integration.id,
    })),
    { label: 'Add New Hubspot Instance', value: NEW_HUBSPOT_INTEGRATION },
  ];

  const navigate = useNavigate();
  const selectHubspotInstance = (hubspotInstance: string) => {
    if (hubspotInstance === NEW_HUBSPOT_INTEGRATION) {
      navigate(`/dashboard/marketplace/hubspot`);
    } else {
      updateActionParams({
        ...params,
        'external-integration-id': hubspotInstance,
      });
    }
  };

  const objectProperties: Record<string, string> = useMemo(
    () => JSON.parse(params['object-properties']),
    [params],
  );

  const associatedObjectTypeOptions = useMemo(
    () => recordObjectOptions.filter((option) => option.value !== params['object-type']),
    [params['object-type'], recordObjectOptions],
  );

  const handleSkipOnFailureCheckbox = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
    updateHubspotObjectParams('skip-on-blank-value', checked);
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof ParamsHubspotOAuthUpdateObjectV1Props['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Hubspot Instance'
        htmlFor='params-hubspot-oauth-update-object__external-integration-id'
      />
      <Select
        value={params['external-integration-id']}
        onValueChange={selectHubspotInstance}
        id='params-hubspot-oauth-update-object__external-integration-id'
        disabled={!editable || isLoading}
        hasError={hasErrorFor('external-integration-id')}
      >
        {hubspotInstanceOptions.map(({ label, value }) => (
          <Option key={`${label}-${value}`} value={value}>
            {label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('external-integration-id')}
      <Spacer height={2} />
      <ParamLabel
        labelText='Object Type'
        htmlFor='params-hubspot-oauth-update-object__object-type'
      />
      <Select
        value={params['object-type']}
        onValueChange={(value: ParamsHubspotOAuthUpdateObjectV1Props['params']['object-type']) => {
          updateHubspotObjectParams('object-type', value);
        }}
        id='params-hubspot-oauth-update-object__object-type'
        disabled={!editable || isLoading}
        hasError={hasErrorFor('object-type')}
      >
        {recordObjectOptions.map(({ label, value }) => (
          <Option key={`${label}-${value}`} value={value}>
            {label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('object-type')}
      <Spacer height={2} />
      <ParamLabel labelText='Object ID' htmlFor='params-hubspot-oauth-update-object__object-id' />
      <HandlebarFormControl
        size='md'
        type='input'
        value={params['object-id'] || ''}
        updateValue={(value: ParamsHubspotOAuthUpdateObjectV1Props['params']['object-id']) => {
          updateHubspotObjectParams('object-id', value);
        }}
        editable={editable}
        allowedTypes={[JsonSchemaTypes.String]}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-hubspot-oauth-update-object__object-id'
      />
      {renderErrorMessageFor('object-id')}
      <Spacer height={2} />
      <InputFieldsHeading>
        <FormControlLabel htmlFor='params-hubspot-oauth-update-object__object-properties'>
          Object Properties
        </FormControlLabel>
        <HStack>
          <Button
            width='icon'
            variant='basic'
            onClick={toggleEditPanel}
            id='params-hubspot-oauth-update-object__object-properties'
            disabled={!editable || isLoading || !params['external-integration-id']}
          >
            <EditIcon />
          </Button>
        </HStack>
      </InputFieldsHeading>
      <Spacer height={2} />
      <HorizontalScrollableBox>
        {isLoading
          ? 'Loading'
          : Object.entries(objectProperties).length === 0
            ? 'No Data'
            : Object.entries(objectProperties).map((keyValuePair, idx) => (
                <Fragment key={idx}>
                  <Row>
                    <Key>
                      {propertiesOptions?.find((int) => int.value === keyValuePair[0])?.label ??
                        keyValuePair[0]}
                    </Key>
                    :<Value>{keyValuePair[1]}</Value>
                  </Row>
                </Fragment>
              ))}
      </HorizontalScrollableBox>
      {renderErrorMessageFor('object-properties')}
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <Spacer height={2} />
        <ParamLabel
          labelText='Associated Object Type'
          htmlFor='params-hubspot-oauth-update-object__associated-object-type'
        />
        <Select
          value={params['associated-object-type']}
          onValueChange={(
            value: ParamsHubspotOAuthUpdateObjectV1Props['params']['associated-object-type'],
          ) => {
            updateHubspotObjectParams('associated-object-type', value);
          }}
          id='params-hubspot-oauth-update-object__associated-object-type'
          disabled={!editable}
          hasError={hasErrorFor('object-type')}
        >
          {associatedObjectTypeOptions.map(({ label, value }) => (
            <Option key={`${label}-${value}`} value={value}>
              {label}
            </Option>
          ))}
        </Select>
        <Spacer height={2} />
        <ParamLabel
          labelText='Associated Object ID'
          htmlFor='params-hubspot-oauth-update-object__associated-object-id'
        />
        <HandlebarFormControl
          size='md'
          type='input'
          value={params['associated-object-id'] || ''}
          updateValue={(
            value: ParamsHubspotOAuthUpdateObjectV1Props['params']['associated-object-id'],
          ) => {
            updateHubspotObjectParams('associated-object-id', value);
          }}
          editable={editable}
          allowedTypes={[JsonSchemaTypes.String]}
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          id='params-hubspot-oauth-update-object__associated-object-id'
        />
        {renderErrorMessageFor('associated-object-id')}
        <Spacer height={2} />
        <Checkbox
          id='params-hubspot-oauth-update-object__skip-on-blank-value'
          label='Do not update Hubspot property with blank value'
          disabled={!editable}
          checked={params['skip-on-blank-value']}
          onChange={handleSkipOnFailureCheckbox}
        />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
      {showEditPanel && (
        <OptionsFieldMappingsPanel
          editable={editable}
          keyValueObject={objectProperties}
          title={`Update ${HubspotObjectTypes[params['object-type']]} Object with These Properties`}
          idPrefix='params-hubspot-oauth-update-object-properties'
          updateObject={(newProperty) =>
            updateHubspotObjectParams('object-properties', JSON.stringify(newProperty))
          }
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          options={propertiesOptions}
          onOutsideClick={closeEditPanel}
        />
      )}
    </>
  );
};

const mapDispatchToProps = {
  loadExternalIntegration,
};

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ParamsHubspotOAuthUpdateObjectV1);
